import { Box, Typography } from "@mui/material";
import CustomCopyButton from "../../../../../../components/CustomCopyButton";
import CustomTooltip from "../../../../../../components/CustomTooltip";
import {
  COMPLEX_ENTITY_2ND_COLUMN_MAP,
  COMPLEX_ENTITY_3RD_COLUMN_MAP,
  ComplexEntityTypes,
} from "../../../../../../utils/constants";
import { getHostLastSeenTimestamp } from "../../../../../../utils/search";
import { getPrettyDate } from "../../../../../../utils/timestamp";
import AddToAssetButton from "../../../../../stack/environment/components/AddToAssetButton";
import LabelsCell from "../../../../../stack/environment/entitiesTable/LabelsCell/LabelsCell";
import TagCell from "../../../../../stack/environment/entitiesTable/TagCell/TagCell";
import TerminatedEntities from "../../EntitiesSection/TerminatedEntities";
import ExclusionInformationIcon from "../groupedControl/misconfigurations/ExclusionInformationIcon";
import SnoozeInformationIcon from "../groupedControl/misconfigurations/SnoozeInformationIcon";

export const getFirstDiscoveredColumn = (colors, valueGetter) => {
  return {
    field: "first_seen",
    headerName: "First Discovered",
    alignHeader: "left",
    align: "left",
    minWidth: 150,
    sortable: true,
    resizable: true,
    disableReorder: true,
    valueGetter,
    renderCell: (params) => {
      return (
        <Typography variant="caption" color={colors.grey_700}>
          {params.value > 0 ? getPrettyDate(params.value) : "N/A"}
        </Typography>
      );
    },
  };
};
export const addToAssetColumn = {
  field: "add_to_asset",
  headerName: "",
  hideHeader: true,
  align: "center",
  width: 50,
  sortable: false,
  resizable: false,
  disableReorder: true,
  renderCell: (params) => {
    return (
      <AddToAssetButton
        entity={params.row}
        diamondStyle={{ cursor: "pointer" }}
        sx={{ paddingBottom: 0.5 }}
      />
    );
  },
};

export const getLabelColumn = () => {
  return {
    field: "labels",
    headerName: "Label",
    minWidth: 120,
    flex: 0.8,
    align: "left",
    headerAlign: "left",
    disableReorder: true,
    valueGetter: (params) => params.row?.labels || [],
    valueFormatter: (params) =>
      params.value.length > 0 ? params.value.join(", ") : "-",
    sortComparator: (v1, v2) => {
      if (v1.length !== v2.length) {
        return v2.length - v1.length;
      }
      const v1Sorted = [...v1].sort();
      const v2Sorted = [...v2].sort();
      return v1Sorted.join(",").localeCompare(v2Sorted.join(","));
    },
    renderCell: (params) => {
      if (params.value.length === 0) {
        return (
          <Typography variant="caption" sx={{ paddingLeft: 1 }}>
            -
          </Typography>
        );
      }
      return (
        <Box
          onClick={(event) => {
            event.stopPropagation();
          }}
          width="100%"
        >
          <LabelsCell entity={params.row} />
        </Box>
      );
    },
  };
};

export const getTagColumn = () => {
  return {
    field: "custom_tags",
    headerName: "Tag",
    minWidth: 120,
    flex: 0.8,
    align: "left",
    headerAlign: "left",
    disableReorder: true,
    valueGetter: (params) => params.row?.custom_tags || [],
    valueFormatter: (params) =>
      params.value.length > 0 ? params.value.join(", ") : "-",
    sortComparator: (v1, v2) => {
      if (v1.length !== v2.length) {
        return v2.length - v1.length;
      }
      const v1Sorted = [...v1].sort();
      const v2Sorted = [...v2].sort();
      return v1Sorted.join(",").localeCompare(v2Sorted.join(","));
    },
    renderCell: (params) => {
      if (params.value.length === 0) {
        return (
          <Typography variant="caption" sx={{ paddingLeft: 1 }}>
            -
          </Typography>
        );
      }
      return (
        <Box
          onClick={(event) => {
            event.stopPropagation();
          }}
          width="100%"
        >
          <TagCell entity={params.row} />
        </Box>
      );
    },
  };
};
export const getDisplayNameColumn = (colors, terminatedEntities) => {
  const users = Object.entries(terminatedEntities.users).map(
    ([id, terminationDate]) => ({
      id,
      terminationDate,
    })
  );
  const hosts = Object.entries(terminatedEntities.hosts).map(
    ([id, terminationDate]) => ({
      id,
      terminationDate,
    })
  );
  const terminatedUsersAndHosts = [...users, ...hosts];

  return {
    field: "display_name",
    headerName: "Name",
    alignHeader: "left",
    align: "left",
    flex: 1,
    minWidth: 180,
    sortable: true,
    resizable: true,
    disableReorder: true,
    renderCell: (params) => {
      return (
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          sx={{
            minWidth: 0,
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <TerminatedEntities
              params={params}
              terminatedEntities={terminatedUsersAndHosts}
            />
          </Box>
          <Box
            sx={{
              overflow: "hidden",
            }}
          >
            <CustomTooltip title="View entity details" placement="top">
              <Typography
                variant="caption"
                color={colors.grey_700}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {params.value}
              </Typography>
            </CustomTooltip>
          </Box>
          <Box onClick={(e) => e.stopPropagation()}>
            <CustomCopyButton copyTitle={"name"} copyText={params.value} />
          </Box>
        </Box>
      );
    },
  };
};

export const getExtraInfoColumn = (entityType, colors) => {
  return {
    field: "extra_info_column",
    headerName: COMPLEX_ENTITY_2ND_COLUMN_MAP[entityType].columnName,
    headerAlign: "left",
    align: "left",
    flex: entityType === ComplexEntityTypes.NONHUMAN_IDENTITY ? 0.3 : 1,
    resizable: true,
    disableReorder: true,
    valueGetter: (params) => {
      if (
        COMPLEX_ENTITY_2ND_COLUMN_MAP[entityType].prop === "original_unique_id"
      )
        return params.row.original_unique_id;
      else if (COMPLEX_ENTITY_2ND_COLUMN_MAP[entityType].prop === "unique_id")
        return params.row.unique_id;
      else if (
        entityType === ComplexEntityTypes.HOST &&
        COMPLEX_ENTITY_2ND_COLUMN_MAP[entityType].prop === "last_seen"
      ) {
        return getHostLastSeenTimestamp(params.row);
      } else if (
        params.row &&
        COMPLEX_ENTITY_2ND_COLUMN_MAP[entityType].prop in params.row
      ) {
        return params.row[COMPLEX_ENTITY_2ND_COLUMN_MAP[entityType].prop];
      } else return "";
    },
    renderCell: (params) => {
      let value = params.value;
      if (
        entityType === ComplexEntityTypes.HOST &&
        COMPLEX_ENTITY_2ND_COLUMN_MAP[entityType].prop === "last_seen"
      )
        value = getPrettyDate(value, false);

      return (
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          sx={{
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              overflow: "hidden",
            }}
          >
            <Typography
              variant="caption"
              color={colors.grey_700}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box onClick={(e) => e.stopPropagation()}>
            <CustomCopyButton
              copyTitle={COMPLEX_ENTITY_2ND_COLUMN_MAP[
                entityType
              ].columnName.toLowerCase()}
              copyText={value}
            />
          </Box>
        </Box>
      );
    },
  };
};

export const getThirdInfoColumn = (entityType, colors) => {
  if (!(entityType in COMPLEX_ENTITY_3RD_COLUMN_MAP)) {
    return null;
  }

  return {
    field: "third_info_column",
    headerName: COMPLEX_ENTITY_3RD_COLUMN_MAP[entityType].columnName,
    headerAlign: "left",
    align: "left",
    flex: 1,
    resizable: true,
    disableReorder: true,
    valueGetter: (params) => {
      if (
        params.row &&
        COMPLEX_ENTITY_3RD_COLUMN_MAP[entityType].prop in params.row
      ) {
        return params.row[COMPLEX_ENTITY_3RD_COLUMN_MAP[entityType].prop];
      }
      return "";
    },
    renderCell: (params) => {
      const value = params.value;

      return (
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          sx={{
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              overflow: "hidden",
            }}
          >
            <Typography
              variant="caption"
              color={colors.grey_700}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box onClick={(e) => e.stopPropagation()}>
            <CustomCopyButton
              copyTitle={COMPLEX_ENTITY_3RD_COLUMN_MAP[
                entityType
              ].columnName.toLowerCase()}
              copyText={value}
            />
          </Box>
        </Box>
      );
    },
  };
};

export const getSimpleEntityDisplayNameColumn = (colors) => {
  return {
    field: "simple_entity_display_name",
    headerName: "Name",
    alignHeader: "left",
    align: "left",
    flex: 1,
    sortable: true,
    resizable: true,
    disableReorder: true,
    minWidth: 90,
    valueGetter: (params) => {
      return params.row.display_name;
    },
    renderCell: (params) => {
      return (
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          sx={{
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              overflow: "hidden",
            }}
          >
            <Typography
              variant="caption"
              color={colors.grey_700}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {params.value}
            </Typography>
          </Box>
          <Box onClick={(e) => e.stopPropagation()}>
            <CustomCopyButton copyTitle={"name"} copyText={params.value} />
          </Box>
        </Box>
      );
    },
  };
};

export const getAffectedEntityInfoIconColumn = (misconfiguration) => {
  return {
    field: "info",
    headerName: "",
    align: "center",
    minWidth: 15,
    sortable: false,
    resizable: false,
    disableReorder: true,
    disableExport: true,
    renderCell: (params) => {
      const rowAffectedEntity = misconfiguration.affected_entities?.find(
        (affected_entity) => affected_entity.entity_id === params.row.entity_id
      );
      if (!rowAffectedEntity?.is_excluded && !rowAffectedEntity?.is_snoozed)
        return null;
      return (
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          sx={{
            minWidth: 0,
          }}
        >
          {rowAffectedEntity?.is_excluded && (
            <ExclusionInformationIcon misconfiguration={rowAffectedEntity} />
          )}
          {rowAffectedEntity?.is_snoozed && (
            <SnoozeInformationIcon misconfiguration={rowAffectedEntity} />
          )}
        </Box>
      );
    },
  };
};
